import { Box, Progress, Spinner, Text, VStack } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { GlobeMethods } from 'react-globe.gl'

import globeBackground from '@/assets/earth-day.jpg'
import { PageHeader } from '@/components/shell'
import { type CustomerMapData, customersApi } from '@/lib/api'

// Preload the globe module - this starts loading immediately when this file is imported
const globeModulePromise = import('react-globe.gl')

// Lazy load the heavy Globe component
const Globe = lazy(() => globeModulePromise)

// Preload the globe texture image
const preloadImage = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve()
    img.onerror = reject
    img.src = src
  })
}

// Start preloading the texture immediately
const texturePreloadPromise = preloadImage(globeBackground)

export const Route = createFileRoute('/_app/valuespace')({
  component: ValueSpacePage,
})

interface GlobeCustomer {
  lat: number
  lng: number
  name: string
  industry: string
  color: string
  slug: string
}

// Industry colors (fallback if customer doesn't have a color set)
const industryColors: Record<string, string> = {
  Automotive: '#3B82F6', // Blue
  'Smart Home': '#10B981', // Green
  'Fleet Management': '#8B5CF6', // Purple
  Energy: '#F59E0B', // Amber
  Utilities: '#06B6D4', // Cyan
  Aviation: '#EC4899', // Pink
  Pharma: '#EF4444', // Red
  Healthcare: '#14B8A6', // Teal
  Marketing: '#F97316', // Orange
  Industrial: '#6366F1', // Indigo
  Logistics: '#84CC16', // Lime
  'Access Control': '#A855F7', // Violet
  Traffic: '#FBBF24', // Yellow
  Telecare: '#22D3EE', // Cyan light
}

// Loading placeholder for the globe
function GlobeLoadingPlaceholder({ status }: { status?: string }) {
  return (
    <Box
      position="absolute"
      inset={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="gray.900"
    >
      <VStack gap={4} w="250px">
        <Box
          w="150px"
          h="150px"
          borderRadius="full"
          bg="gray.800"
          display="flex"
          alignItems="center"
          justifyContent="center"
          css={{
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 0.6 },
              '50%': { opacity: 1 },
            },
          }}
        >
          <Spinner size="xl" color="blue.400" borderWidth="4px" />
        </Box>
        <VStack gap={2} w="100%">
          <Text color="gray.400" fontSize="sm">
            {status || 'Loading globe...'}
          </Text>
          <Progress.Root w="100%" size="sm" value={null}>
            <Progress.Track bg="gray.700">
              <Progress.Range bg="blue.400" />
            </Progress.Track>
          </Progress.Root>
        </VStack>
      </VStack>
    </Box>
  )
}

function ValueSpacePage() {
  const globeRef = useRef<GlobeMethods | undefined>(undefined)
  const globeContainerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [hoveredCustomer, setHoveredCustomer] = useState<GlobeCustomer | null>(null)
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 })
  const [isGlobeReady, setIsGlobeReady] = useState(false)
  const [loadingStatus, setLoadingStatus] = useState('Initializing...')
  const [assetsLoaded, setAssetsLoaded] = useState(false)
  const navigate = useNavigate()

  // Fetch customers from API
  const { data: apiCustomers, isLoading } = useQuery({
    queryKey: ['customers', 'map'],
    queryFn: () => customersApi.getForMap(),
  })

  // Transform API customers to globe format
  const customerList: GlobeCustomer[] = useMemo(() => {
    if (!apiCustomers) {
      return []
    }
    return apiCustomers.map((c: CustomerMapData) => ({
      lat: c.latitude,
      lng: c.longitude,
      name: c.name,
      industry: c.industry,
      color: c.color || industryColors[c.industry] || '#3B82F6',
      slug: c.slug,
    }))
  }, [apiCustomers])

  // Get unique industries for the legend
  const uniqueIndustries = useMemo(() => {
    return [...new Set(customerList.map((c) => c.industry))].sort()
  }, [customerList])

  // Preload assets (module + texture) in parallel
  useEffect(() => {
    let mounted = true

    const loadAssets = async () => {
      try {
        setLoadingStatus('Loading 3D engine...')

        // Wait for both module and texture to load in parallel
        await Promise.all([
          globeModulePromise.then(() => {
            if (mounted) {
              setLoadingStatus('Loading textures...')
            }
          }),
          texturePreloadPromise,
        ])

        if (mounted) {
          setLoadingStatus('Preparing globe...')
          setAssetsLoaded(true)
        }
      } catch (error) {
        console.error('Failed to load globe assets:', error)
        if (mounted) {
          setLoadingStatus('Error loading globe')
        }
      }
    }

    loadAssets()

    return () => {
      mounted = false
    }
  }, [])

  // Track mouse position for tooltip
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({ x: e.clientX, y: e.clientY })
    }
    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  // Handle resize to update globe dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (globeContainerRef.current) {
        const rect = globeContainerRef.current.getBoundingClientRect()
        if (rect.width > 0 && rect.height > 0) {
          setDimensions({ width: rect.width, height: rect.height })
        }
      }
    }

    updateDimensions()
    window.addEventListener('resize', updateDimensions)

    // Multiple retries to catch late layout
    const timers = [
      setTimeout(updateDimensions, 100),
      setTimeout(updateDimensions, 300),
      setTimeout(updateDimensions, 500),
    ]

    return () => {
      window.removeEventListener('resize', updateDimensions)
      timers.forEach(clearTimeout)
    }
  }, [])

  // Defer globe rendering until assets are loaded
  useEffect(() => {
    if (!assetsLoaded) {
      return
    }

    // Small delay to ensure smooth transition
    const timer = setTimeout(() => {
      setIsGlobeReady(true)
      setLoadingStatus('Rendering...')
    }, 50)

    return () => clearTimeout(timer)
  }, [assetsLoaded])

  // Set initial position and auto-rotate the globe. `dimensions` is intentionally a
  // dependency so this re-arms whenever the container is (re)measured (globe becomes
  // ready, or the window/layout resizes) - pre-existing behavior, not touched by this restyle.
  // biome-ignore lint/correctness/useExhaustiveDependencies: dimensions dependency is intentional, see comment above
  useEffect(() => {
    if (globeRef.current) {
      // Set initial view to focus on Atlantic (between US and EMEA)
      // Longitude -30 centers on Atlantic Ocean
      // Latitude 35 gives good view of both US and Europe
      globeRef.current.pointOfView(
        {
          lat: 35,
          lng: -30,
          altitude: 2.2,
        },
        0,
      ) // 0ms transition for immediate positioning

      // Enable auto-rotation
      globeRef.current.controls().autoRotate = true
      // Slower rotation speed to keep EMEA/US regions visible longer
      globeRef.current.controls().autoRotateSpeed = 0.3
    }
  }, [dimensions])

  const handlePointHover = useCallback((point: object | null) => {
    if (point) {
      const customer = point as GlobeCustomer
      setHoveredCustomer(customer)
      // Pause rotation on hover
      if (globeRef.current) {
        globeRef.current.controls().autoRotate = false
      }
    } else {
      setHoveredCustomer(null)
      // Resume rotation
      if (globeRef.current) {
        globeRef.current.controls().autoRotate = true
      }
    }
  }, [])

  const handlePointClick = useCallback(
    (point: object) => {
      const customer = point as GlobeCustomer
      navigate({
        to: '/customers/$slug',
        params: { slug: customer.slug },
        search: { from: '/valuespace' },
      })
    },
    [navigate],
  )

  // Show loading state
  if (isLoading) {
    return (
      <Box h="calc(100vh - 64px)" display="flex" alignItems="center" justifyContent="center">
        <VStack gap={4}>
          <Spinner size="xl" />
          <Text color="gray.500">Loading customers...</Text>
        </VStack>
      </Box>
    )
  }

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        title="Value Space"
        subtitle="Explore HiveMQ customers around the world. Hover over a point to see details, click to learn more."
      />

      <VStack align="stretch" gap={10}>
        {/* Globe (3D) */}
        <Box
          position="relative"
          h="calc(100vh - 260px)"
          minH="500px"
          w="100%"
          overflow="hidden"
          borderRadius="xl"
          bg="gray.900"
        >
          {/* Globe container */}
          <Box ref={globeContainerRef} position="absolute" inset={0}>
            {isGlobeReady && dimensions.width > 0 && dimensions.height > 0 ? (
              <Suspense fallback={<GlobeLoadingPlaceholder status={loadingStatus} />}>
                <Globe
                  ref={globeRef}
                  width={dimensions.width}
                  height={dimensions.height}
                  globeImageUrl={globeBackground}
                  backgroundColor="rgba(0,0,0,0)"
                  atmosphereColor="#3B82F6"
                  atmosphereAltitude={0.15}
                  pointsData={customerList}
                  pointLat="lat"
                  pointLng="lng"
                  pointColor="color"
                  pointAltitude={0.01}
                  pointRadius={0.5}
                  pointsMerge={false}
                  onPointHover={handlePointHover}
                  onPointClick={handlePointClick}
                />
              </Suspense>
            ) : (
              <GlobeLoadingPlaceholder status={loadingStatus} />
            )}
          </Box>

          {/* Tooltip */}
          {hoveredCustomer && (
            <Box
              position="fixed"
              left={`${mousePos.x + 15}px`}
              top={`${mousePos.y - 10}px`}
              bg="gray.800"
              border="1px solid"
              borderColor="gray.600"
              borderRadius="lg"
              px={4}
              py={3}
              zIndex={1000}
              pointerEvents="none"
              boxShadow="xl"
              minW="180px"
            >
              <Text color="white" fontWeight="bold" fontSize="md">
                {hoveredCustomer.name}
              </Text>
              <Box display="inline-flex" alignItems="center" gap={2} mt={1}>
                <Box w={3} h={3} borderRadius="full" bg={hoveredCustomer.color} />
                <Text color="gray.300" fontSize="sm">
                  {hoveredCustomer.industry}
                </Text>
              </Box>
              <Text color="gray.500" fontSize="xs" mt={2}>
                Click to view details
              </Text>
            </Box>
          )}

          {/* Legend */}
          <Box
            position="absolute"
            bottom={6}
            left={6}
            bg="blackAlpha.700"
            backdropFilter="blur(10px)"
            px={5}
            py={4}
            borderRadius="xl"
            zIndex={10}
            maxW="280px"
          >
            <Text color="white" fontWeight="bold" mb={3} fontSize="sm">
              Industries ({customerList.length} customers)
            </Text>
            <VStack align="start" gap={1.5}>
              {uniqueIndustries.map((industry) => (
                <Box key={industry} display="flex" alignItems="center" gap={2}>
                  <Box
                    w={3}
                    h={3}
                    borderRadius="full"
                    bg={industryColors[industry]}
                    flexShrink={0}
                  />
                  <Text color="gray.300" fontSize="xs">
                    {industry}
                  </Text>
                </Box>
              ))}
            </VStack>
          </Box>

          {/* Instructions */}
          <Box
            position="absolute"
            bottom={6}
            right={6}
            bg="blackAlpha.600"
            backdropFilter="blur(10px)"
            px={4}
            py={2}
            borderRadius="full"
            zIndex={10}
          >
            <Text color="whiteAlpha.700" fontSize="sm">
              Drag to rotate • Scroll to zoom
            </Text>
          </Box>
        </Box>
      </VStack>
    </Box>
  )
}
